import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { ISelectFieldModel } from "../../../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { useApiMutation } from "../../../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../../../../common/infrastructure/utilities/dateUtility";
import { humanizePaymentType } from "../../../../../../../common/utilities/paymentUtility";
import { ICardRenewalOptionModel } from "../models/cardRenewalOptionModel";
import {
  IRenewCardFormModel,
  renewCardFormDefaultValues,
  renewCardFormSchema,
} from "../models/renewCardFormModel";
import {
  cardRenewalOptionsQueryKey,
  useCardRenewalOptions,
} from "./useCardRenewalOptions";

interface IUseRenewCardDialogProps {
  userId?: number;
  visible: boolean;
  onHide: () => void;
}

const DATE_FORMAT = "dd.MM.yyyy";

/** "8 посещения", "1 посещение", "Неограничени посещения" for a card without a limit. */
export const formatRenewalVisits = (visits?: number | null): string => {
  if (visits === null || visits === undefined) {
    return "Неограничени посещения";
  }
  return `${visits} ${visits === 1 ? "посещение" : "посещения"}`;
};

/** "Групови 1 месец (Групови)": what the picker lists when the client has several cards. */
export const formatRenewalOptionLabel = (option: ICardRenewalOptionModel) =>
  option.planTypeName
    ? `${option.planName} (${option.planTypeName})`
    : option.planName;

export const formatRenewalValidity = (option: ICardRenewalOptionModel) =>
  `Валидна: ${dateUtility.formatDate(option.newStartDate, DATE_FORMAT)} – ${dateUtility.formatDate(option.newEndDate, DATE_FORMAT)}`;

/** Only for an early renewal: the new card waits for the current one to end. */
export const formatRenewalStartNote = (option: ICardRenewalOptionModel) =>
  option.isCurrentCardActive
    ? `Текущата карта е валидна до ${dateUtility.formatDate(option.currentCardActiveUntil, DATE_FORMAT)}, затова новата започва след нея.`
    : undefined;

/**
 * "Поднови карта": the client's last card (of the picked plan type when there are several)
 * is sold again with the same plan, price, visits and payment type. The server works out the
 * dates and the seller; this only shows them, then posts the pick.
 */
export const useRenewCardDialog = ({
  userId,
  visible,
  onHide,
}: IUseRenewCardDialogProps) => {
  const form = useForm<IRenewCardFormModel>({
    defaultValues: renewCardFormDefaultValues,
    resolver: yupResolver(renewCardFormSchema),
  });

  const {
    data,
    isFetching,
    error: loadError,
  } = useCardRenewalOptions(userId, visible);

  const options = useMemo(() => data ?? [], [data]);

  // The newest card is picked on open; a pick that is no longer offered falls back to it.
  useEffect(() => {
    if (!visible) {
      form.reset(renewCardFormDefaultValues);
      return;
    }
    const current = form.getValues("subscriptionId");
    if (
      options.length > 0 &&
      !options.some((option) => option.subscriptionId === current)
    ) {
      form.setValue("subscriptionId", options[0].subscriptionId);
    }
  }, [visible, options, form]);

  const subscriptionId = form.watch("subscriptionId");
  const selectedOption = options.find(
    (option) => option.subscriptionId === subscriptionId,
  );

  const selectOptions: ISelectFieldModel[] = useMemo(
    () =>
      options.map((option) => ({
        id: option.subscriptionId,
        name: formatRenewalOptionLabel(option),
      })),
    [options],
  );

  const isOptionsLoading = visible && isFetching && !data;

  const loadErrorMessage = useMemo(() => {
    if (!visible || !loadError || isFetching) {
      return undefined;
    }
    if (axios.isAxiosError(loadError)) {
      const message = (loadError.response?.data as { message?: unknown })
        ?.message;
      if (typeof message === "string" && message) {
        return message;
      }
    }
    return "Неуспешно зареждане на картите. Моля опитайте отново.";
  }, [visible, loadError, isFetching]);

  const isEmpty = visible && !!data && options.length === 0;

  const summary = selectedOption
    ? {
        planName: selectedOption.planName,
        planTypeName: selectedOption.planTypeName ?? undefined,
        visitsLabel: formatRenewalVisits(selectedOption.visits),
        paymentTypeLabel: humanizePaymentType(selectedOption.paymentType),
        validityLabel: formatRenewalValidity(selectedOption),
        startNote: formatRenewalStartNote(selectedOption),
        pricePaid: selectedOption.pricePaid,
        currencyId: selectedOption.currencyId,
      }
    : undefined;

  const { mutate, isLoading: isRenewing } = useApiMutation();

  const canRenew = !!userId && !!selectedOption && !isRenewing;

  const handleRenew = form.handleSubmit((values) => {
    if (!userId) {
      return;
    }

    mutate(
      {
        endpoint: endpointsMetadata.plan.renewCard,
        method: "post",
        payload: { userId, subscriptionId: values.subscriptionId },
        invalidateKeys: [
          [endpointsMetadata.user.getUser, String(userId)],
          cardRenewalOptionsQueryKey(userId),
          [endpointsMetadata.userAuditLog.getUserHistory, userId],
        ],
      },
      { onSuccess: () => onHide() },
    );
  });

  return {
    form,
    selectOptions,
    hasSeveralOptions: options.length > 1,
    summary,
    isOptionsLoading,
    loadErrorMessage,
    isEmpty,
    canRenew,
    isRenewing,
    handleRenew,
  };
};
